import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';

const HANDLE = 56;
const PAD = 4;
const THRESHOLD = 0.7;

// Swipe-to-confirm control. mode: 'checkin' | 'checkout' | 'done'.
// Drag the handle across the track to confirm — replaces press-buttons.
const SwipeAttendance = ({ mode, disabled, busy, onConfirm }) => {
  const trackRef = useRef(null);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [hovering, setHovering] = useState(false);

  const isCheckout = mode === 'checkout';
  const locked = disabled || busy || mode === 'done';

  const getMax = () => {
    const track = trackRef.current;
    return track ? track.offsetWidth - HANDLE - PAD * 2 : 0;
  };

  useEffect(() => { setDragX(0); }, [mode]);

  const handlePointerDown = (e) => {
    if (locked) return;
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!dragging) return;
    const rect = trackRef.current.getBoundingClientRect();
    const max = getMax();
    let x = isCheckout ? (rect.right - PAD - HANDLE - e.clientX) : (e.clientX - rect.left - PAD);
    x = Math.max(0, Math.min(max, x));
    setDragX(x);
  };

  const handlePointerUp = () => {
    if (!dragging) return;
    setDragging(false);
    const max = getMax();
    if (max > 0 && dragX / max >= THRESHOLD) {
      onConfirm();
    } else {
      setDragX(0);
    }
  };

  if (mode === 'done') {
    return (
      <div className="w-full max-w-sm h-16 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center gap-2 shadow-soft transition-shadow hover:shadow-md">
        <CheckCircle2 className="w-5 h-5 text-gray-400" />
        <span className="text-xs font-black uppercase tracking-widest text-gray-400">Shift Completed</span>
      </div>
    );
  }

  const max = getMax();
  const progress = max > 0 ? dragX / max : 0;
  const trackGradient = isCheckout
    ? 'from-amber-500 to-amber-600'
    : 'from-green-500 to-green-600';

  return (
    <div
      ref={trackRef}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className={
        'relative w-full max-w-sm h-16 rounded-2xl bg-gray-50 border border-gray-100 overflow-hidden select-none transition-all duration-300 ' +
        (locked ? 'opacity-50' : (hovering ? 'shadow-elevated' : 'shadow-soft'))
      }
    >
      {/* Fill */}
      <div
        className={
          'absolute inset-y-0 bg-gradient-to-r ' + trackGradient + (isCheckout ? ' right-0' : ' left-0')
        }
        style={{
          width: HANDLE + PAD * 2 + dragX,
          transition: dragging ? 'none' : 'width 300ms ease',
        }}
      />

      {/* Label */}
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-12 sm:px-16">
        <span
          className="text-xs font-black uppercase tracking-widest transition-opacity duration-200"
          style={{ opacity: 1 - progress * 0.6, color: progress > 0.4 ? '#fff' : '#111827' }}
        >
          {isCheckout ? 'Check Out' : 'Check In'}
        </span>
        <span
          className="text-[10px] font-semibold transition-opacity duration-200"
          style={{ opacity: 1 - progress * 0.6, color: progress > 0.4 ? 'rgba(255,255,255,0.85)' : '#9ca3af' }}
        >
          {isCheckout ? 'Swipe left to end your work day' : 'Swipe right to start your work day'}
        </span>
      </div>

      {/* Handle */}
      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          width: HANDLE,
          height: HANDLE,
          touchAction: 'none',
          [isCheckout ? 'right' : 'left']: PAD + dragX,
          transition: dragging ? 'none' : 'left 300ms ease, right 300ms ease, transform 150ms ease, box-shadow 150ms ease',
        }}
        className={
          'absolute top-1/2 -translate-y-1/2 rounded-full bg-white shadow-elevated flex items-center justify-center ' +
          (locked ? 'pointer-events-none' : 'cursor-grab active:cursor-grabbing hover:scale-110 hover:shadow-2xl active:scale-95')
        }
      >
        {busy ? (
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        ) : isCheckout ? (
          <ArrowLeft className="w-5 h-5 text-amber-600" />
        ) : (
          <ArrowRight className="w-5 h-5 text-green-600" />
        )}
      </div>
    </div>
  );
};

export default SwipeAttendance;